import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { WorkspaceDocument } from '../../Models/Workspace'
import { useAppSelector } from '../../redux/hooks'
import { selectAllTasks } from '../../redux/tasks'
import { selectLinkedTasks } from '../../redux/workspace'
import styles from './WorkspaceLinkedTasks.module.css'

type WorkspaceLinkedTasksProps = {
  document: WorkspaceDocument
  onLink: (taskId: string) => void
  onUnlink: (taskId: string) => void
}

const WorkspaceLinkedTasks = ({ document, onLink, onUnlink }: WorkspaceLinkedTasksProps) => {
  const navigate = useNavigate()
  const allTasks = useAppSelector(selectAllTasks)
  const linkedTasks = useAppSelector(selectLinkedTasks(document.linkedTaskIds))
  const [isLinking, setIsLinking] = useState(false)

  const availableTasks = useMemo(
    () =>
      allTasks
        .filter((task) => !document.linkedTaskIds.includes(task.id))
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })),
    [allTasks, document.linkedTaskIds]
  )

  const sortedLinkedTasks = useMemo(
    () =>
      linkedTasks
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })),
    [linkedTasks]
  )

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <p className={styles.title}>Linked Tasks</p>
        <button
          type="button"
          className={styles.actionBtn}
          onClick={() => setIsLinking((open) => !open)}
        >
          {isLinking ? 'Cancel' : 'Link task'}
        </button>
      </div>

      {isLinking ? (
        availableTasks.length > 0 ? (
          <ul className={styles.list}>
            {availableTasks.map((task) => (
              <li key={`pick-task-${task.id}`}>
                <button
                  type="button"
                  className={styles.itemButton}
                  onClick={() => {
                    onLink(task.id)
                    setIsLinking(false)
                  }}
                >
                  + {task.title}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyText}>All tasks are already linked.</p>
        )
      ) : null}

      {sortedLinkedTasks.length > 0 ? (
        <ul className={styles.list}>
          {sortedLinkedTasks.map((task) => (
            <li key={task.id}>
              <div className={styles.linkedRow}>
                <button
                  type="button"
                  className={styles.itemButton}
                  onClick={() => navigate(`/tasks/${task.id}`)}
                >
                  {task.title}
                </button>
                <button
                  type="button"
                  className={styles.unlinkBtn}
                  aria-label={`Unlink ${task.title}`}
                  onClick={() => onUnlink(task.id)}
                >
                  ×
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className={styles.emptyText}>No tasks linked yet.</p>
      )}
    </section>
  )
}

export default WorkspaceLinkedTasks
